import { Box, Spinner, Text } from '@chakra-ui/react';
import { useState } from 'react';
import { useListContracts } from 'hooks/useApiQueries';
import { useLocation, useParams } from 'react-router-dom';
import type { MergedPricingContract } from 'screens/Prices/utils/mergePricingService';
import { MindbodyContract } from 'types/contract';
import { ApiClientException } from 'utils/apiClientException';
import { HTTP } from 'utils/http';
import { resolveUserError } from 'utils/userErrorMessages';
import { setScaPayment, getCurrentScaAttemptId, getOrCreateDeviceId } from 'utils/scaStorage';
import * as Sentry from '@sentry/react';
import { sentryTrace } from 'utils/sentryTrace';
import { ContractPaymentOverlay, ContractPaymentStep } from 'components/ContractPaymentOverlay';
import { CheckoutData, CheckoutForm } from './CheckoutForm';

export interface ContractCheckoutProps {
  /** Contrat transmis par la page Tarifs via `location.state` ou `props`. */
  contract?: MindbodyContract;
}

interface ContractPurchaseResponse extends Record<string, unknown> {
  sca3DSecureRequired?: boolean;
  scaAuthenticationUrls?: string[];
  ClientContractId?: number | null;
  Error?: { Message: string; Code: string };
}

/**
 * Wrapper du flow d'achat d'un contract. Branche `CheckoutForm` en mode
 * `variant='contract'` (Alma masqué, carte cadeau masquée, SaveInfo forcée
 * cochée + désactivée). Affiche un `ContractPaymentOverlay` pendant les
 * phases d'appel API pour rassurer l'utilisateur.
 */
export function ContractCheckout({ contract: propContract }: ContractCheckoutProps) {
  const { contractId } = useParams<{ contractId: string }>();
  const location = useLocation();
  const { data: allContracts, isLoading } = useListContracts();
  const [overlayStep, setOverlayStep] = useState<ContractPaymentStep>('idle');
  const [overlayError, setOverlayError] = useState<string | null>(null);

  const stateContract: MindbodyContract | undefined = location.state?.contract;
  const stateMerged: MergedPricingContract | undefined = location.state?.merged;
  const idNum = contractId ? Number(contractId) : null;
  const fromList = idNum && allContracts ? allContracts.find((c) => c.Id === idNum) : undefined;
  const contract = propContract ?? stateContract ?? fromList;

  // Le 1er paiement = FirstPaymentAmountTotal (souvent identique au recurring)
  const firstPaymentAmount = contract?.FirstPaymentAmountTotal ?? contract?.RecurringPaymentAmountTotal ?? 0;

  async function onPurchase(payment: CheckoutData): Promise<Record<string, unknown>> {
    if (!contract) throw new Error('Contract introuvable');

    setOverlayStep('validating');
    setOverlayError(null);

    try {
      const response = (await HTTP.post('/sale/purchasecontract', {
        ContractId: contract.Id,
        // LocationId : la même que pour la page Tarifs (defaultLocationId admin).
        LocationId: mondayMindbody?.pricingConfig?.defaultLocationId ?? 1,
        Payment: payment,
        Test: false,
      }).send()) as ContractPurchaseResponse;

      // Gestion SCA : Mindbody n'expose pas de TransactionId pour
      // `purchasecontract` — la finalisation après 3DS est un re-post du
      // même payload (cf. extractContractScaInfo côté serveur).
      if (response?.sca3DSecureRequired) {
        const scaContractData = {
          ...payment,
          ContractId: contract.Id,
        };
        const scaPersisted = setScaPayment('scaContractPaymentData', scaContractData, 'contract');

        // Amorce 3DS (contract) : breadcrumb dans le cas nominal, event seulement
        // si la persistance a échoué (voir useCheckoutForm pour le pourquoi).
        // L'attempt_id a été généré par CheckoutForm (il est dans la
        // ScaReturnUrl du payload) : on reprend le même pour la corrélation.
        const scaInitiated = {
          tags: {
            flow: 'sca',
            sca_stage: 'persist',
            payment_kind: 'contract',
            sca_attempt: getCurrentScaAttemptId() ?? 'none',
            device_id: getOrCreateDeviceId() ?? 'none',
            persisted_local: String(scaPersisted.local),
            persisted_session: String(scaPersisted.session),
          },
          extra: { persisted: scaPersisted },
        };
        if (scaPersisted.local || scaPersisted.session) {
          sentryTrace('SCA: 3DS redirect initiated', scaInitiated);
        } else {
          Sentry.captureMessage('SCA: 3DS redirect initiated', { level: 'warning', ...scaInitiated });
        }

        const urls = response.scaAuthenticationUrls ?? [];
        if (urls.length > 0) {
          setOverlayStep('authenticating');
          window.location.href = urls[0];
        }
        return response as Record<string, unknown>;
      }

      // Succès direct (pas de SCA). CheckoutForm.handleSuccessfulPayment
      // s'occupe du toast et de la redirection vers /account#mon-abonnement.
      setOverlayStep('success');
      return response as Record<string, unknown>;
    } catch (err) {
      if (err instanceof ApiClientException) {
        setOverlayError(err.getMessage());
      } else {
        const { title, description } = resolveUserError('contract', err);
        setOverlayError([title, description].filter(Boolean).join('\n\n'));
      }
      setOverlayStep('failed');
      // Re-throw pour que CheckoutForm affiche aussi son toast d'erreur.
      throw err;
    }
  }

  if (!contract && isLoading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minH="50vh">
        <Spinner size="xl" />
      </Box>
    );
  }

  if (!contract) {
    return (
      <Box textAlign="center" py={12}>
        <Text>Contrat introuvable.</Text>
      </Box>
    );
  }

  // Adapter le contract en ContractProduct (type discriminé) pour CheckoutForm
  const contractProduct = { ...contract, type: 'contract' as const };

  return (
    <>
      <ContractPaymentOverlay
        isOpen={overlayStep !== 'idle'}
        currentStep={overlayStep}
        errorMessage={overlayError}
      />
      <CheckoutForm
        onPurchase={onPurchase}
        amount={firstPaymentAmount}
        product={contractProduct}
        variant="contract"
        contractAgreement={{
          pdfUrl: stateMerged?.contractPdfUrl ?? null,
          agreementTerms: contract.AgreementTerms ?? null,
        }}
      />
    </>
  );
}
